import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { BasicModal, Skeleton } from "@life-line-lab/components-react";
import moment from "moment";
import { find, map, isEmpty } from "lodash";
import { Button } from "@life-line-lab/components-react";
import { Row, Col, Steps, Divider } from "antd";
import { CheckCircleTwoTone, CloseCircleOutlined } from "@ant-design/icons";
import useSession from "Hooks/session.hook";
import { EditRouteModal } from "../parcel-tracking-modals/edit-route-modal";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { routeToTrolleyView } from "Helpers/index";

interface TrolleyParcel {
  id: string;
  // other properties
}

const isComponentLoading = (info, data) => {
  return <>{isEmpty(info) ? <Skeleton /> : data}</>;
};

const ParcelTrackingInfo = ({ info }) => {
  const { t } = useTranslation();

  return (
    <Row gutter={[0, 16]} className="dme-tr-view-info">
      <Col span={24} className="main-title">
        {t("txt.journeyInformation")}:
      </Col>
      <Col span={24}>
        <Row gutter={[32, 0]}>
          <Col span={12}>
            <Row gutter={[0, 4]}>
              <Col span={6} className="label">
                {t("label.trainNo")}:
              </Col>
              <Col span={18}>
                {isComponentLoading(
                  info,
                  info?.plateNo ? info.plateNo : "To be updated.",
                )}
              </Col>
              <Col span={6} className="label">
                {t("label.createdAt")}:
              </Col>
              <Col span={18}>
                {isComponentLoading(
                  info,
                  info?.createdAt
                    ? moment(info.createdAt).format("DD-MM-YYYY")
                    : "To be updated.",
                )}
              </Col>
              <Col span={6} className="label">
                {t("label.updatedAt")}:
              </Col>
              <Col span={18}>
                {isComponentLoading(
                  info,
                  info?.updatedAt
                    ? moment(info.updatedAt).format("DD-MM-YYYY")
                    : "To be updated.",
                )}
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row gutter={[0, 4]}>
              <Col span={8} className="label">
                {t("label.departingFrom")}:
              </Col>
              <Col span={16}>
                {isComponentLoading(
                  info,
                  info?.startStation
                    ? info?.trainLineData?.stations.map(
                        (station) =>
                          station?.stationCode == info?.startStation &&
                          `${info?.startStation} (${station?.stationName})`,
                      )
                    : "To be updated.",
                )}
              </Col>
              <Col span={8} className="label">
                {t("label.initialDepartureTime")}:
              </Col>
              <Col span={16}>
                {isComponentLoading(
                  info,
                  info?.plannedDeparture
                    ? moment(info.plannedDeparture).format("DD-MM-YYYY hh:mm a")
                    : "To be updated.",
                )}
              </Col>
              <Col span={8} className="label">
                {t("label.arrivingAt")}:
              </Col>
              <Col span={16}>
                {isComponentLoading(
                  info,
                  info?.lastStation
                    ? info?.trainLineData?.stations.map(
                        (station) =>
                          station?.stationCode == info?.lastStation &&
                          `${info?.lastStation} (${station?.stationName})`,
                      )
                    : "To be updated.",
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const TrainLineInfo = ({ info, trainSteps, handleOpenEditRoute }) => {
  const { t } = useTranslation();
  const { roleTypes } = useSession();

  const isNotCompleted = info?.status == "COMPLETED" ? false : true;

  const Title = () => {
    return (
      <>
        {t("label.plannedRoute")}
        <span className="train-line-type">{info?.trainLineData?.name}</span>
      </>
    );
  };

  const EditButton = () => {
    return (
      <Button
        type="primary"
        label={t("action.editRoute")}
        size="large"
        onClick={handleOpenEditRoute}
      />
    );
  };

  return (
    <div className="mt-40 dme-tr-view-info">
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row gutter={[16, 0]}>
            <Col flex="auto" className="main-title title-edit-btn">
              {isComponentLoading(info, <Title />)}
            </Col>
            <Col flex="none">
              {roleTypes == "COMPANY-SUPER_ADMIN" &&
                isNotCompleted &&
                isComponentLoading(info, <EditButton />)}
            </Col>
          </Row>
        </Col>
        <Col span={24} className="stepper-scroll">
          <Steps labelPlacement="vertical">
            {isComponentLoading(info, trainSteps)}
          </Steps>
        </Col>
      </Row>
    </div>
  );
};

const TrainLineDetails = ({ info, stop }) => {
  return (
    <Row gutter={[0, 0]} className="station-details">
      <Col span={24} className="station-code">
        {stop?.stationCode}
      </Col>
      <Col span={24} className="station-label">
        {info?.trainLineData?.stations.map(
          (station) =>
            station.stationCode === stop?.stationCode && station.stationName,
        )}
      </Col>
    </Row>
  );
};

const ParcelTrackingEnrouteViewComponent = ({ info, onBack, modalPackage }) => {
  const history = useHistory();
  const location = useLocation();
  const { Step } = Steps;

  const { status, journeyCode, trainStops, trolleyParcelData } = info;
  const {
    setCallModal,
    handleResetModalState,
    handleUpdateRoute,
    setModal,
    modal,
    callModal,
  } = modalPackage || {};

  const handleOpenEditRoute = () => {
    setCallModal(
      <EditRouteModal
        handleCancel={handleResetModalState}
        handleUpdateRoute={handleUpdateRoute}
        info={info}
      />,
    );
    setModal({ open: true });
  };

  const handleOpenTrolleyView = (trolleyID) => {
    // pass this in because we want to detect where the onBack leads to at
    // trolley-view-component.tsx
    const params = "redirect=enroute";
    routeToTrolleyView(trolleyID, location, history, params);
  };

  const getTrolleyObjByID = (trolleyID) => {
    let trolleyObj = find(trolleyParcelData, (parcelObj) => {
      return parcelObj._id == trolleyID;
    });

    return trolleyObj;
  };

  const trainSteps = map(trainStops, (stop, index) => {
    let statusIcon: React.ReactNode | null;
    let statusMessage: string;
    let stepStatus;
    let trolleyParcels: TrolleyParcel[] = [];
    if (stop.loadTrolleys) {
      trolleyParcels = trolleyParcels.concat(stop.loadTrolleys.trolleyParcels);
    }
    if (stop.unloadTrolleys) {
      trolleyParcels = trolleyParcels.concat(
        stop.unloadTrolleys.trolleyParcels,
      );
    }

    switch (stop.status) {
      case "ARRIVED":
        stepStatus = "process";
        statusMessage = `Arrived at`;
        break;
      case "WAITING":
        stepStatus = "wait";
        // statusIcon = <ClockCircleTwoTone />;
        statusMessage = `Waiting at`;
        break;
      case "DEPARTED":
        stepStatus = "finish";
        statusIcon = (
          <CheckCircleTwoTone
            twoToneColor="#52c41a"
            className="train-line-icon"
          />
        );
        statusMessage = "Departed";
        break;
      default:
        stepStatus = "wait";
        statusIcon = null;
        statusMessage = "Waiting at";
    }

    return (
      <Step
        key={index}
        title={statusMessage}
        subTitle={<TrainLineDetails info={info} stop={stop} />}
        description={map(trolleyParcels, (trolley) => (
          <li key={trolley.id}>
            <a
              className="clickable-id"
              onClick={() => {
                handleOpenTrolleyView(trolley.id);
              }}
            >
              {getTrolleyObjByID(trolley.id)?.trolleyCode || "-"}
            </a>
          </li>
        ))}
        status={stepStatus}
        icon={statusIcon}
        disabled
      />
    );
  });

  return (
    <div className="parcel-tracking-view enroute-view">
      <div className="main-header"></div>
      <Row className="status-label" gutter={[16, 16]} align="bottom">
        <Col flex="none">
          <div className="journey-code">
            {isComponentLoading(info, <h3>{journeyCode}</h3>)}
          </div>
        </Col>
        <Col flex="none">
          <span className="status-indicator" style={getStatusStyle(status)}>
            {status && toCapitalisedSpaced(status)}
          </span>
        </Col>
        <Col flex="auto"></Col>
        <Col flex="none">
          <CloseCircleOutlined
            onClick={() => {
              onBack();
            }}
            style={{ fontSize: "20px" }}
          />
        </Col>
      </Row>
      <Divider />
      <ParcelTrackingInfo info={info} />
      <Divider />
      <TrainLineInfo
        info={info}
        trainSteps={trainSteps}
        handleOpenEditRoute={handleOpenEditRoute}
      />
      <BasicModal
        className="modal"
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
        width={"50vw"}
      >
        {callModal}
      </BasicModal>
    </div>
  );
};

export default ParcelTrackingEnrouteViewComponent;
